import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ticket, CalendarX } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const KIND = { booking: "Appointment", registration: "Event / class", order: "Shop order" };

// Gift card redemptions with what they paid for and who delivered it. `admin` adds totals + expiry tool.
export const GiftRedemptionsTable = ({ endpoint, admin = false, title = "Gift card redemptions" }) => {
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["gift-redemptions", endpoint, q], queryFn: () => api.get(endpoint, { params: admin && q ? { q } : {} }).then((r) => r.data) });
  const rows = data?.redemptions || [];
  return (
    <section className="eyw-card p-6 mt-8" data-testid="gift-redemptions">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Ticket size={17} className="text-[#D98E73]" /> {title}</h3>
        {admin && <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, enhancer, center" className="eyw-input !py-1.5 text-sm ml-auto w-64" data-testid="gift-redemptions-search" />}
      </div>
      <div className="flex flex-wrap gap-6 mb-4 text-sm" data-testid="gift-redemptions-totals">
        <span>Redeemed: <b>{money(data?.total_redeemed ?? data?.total ?? 0)}</b></span>
        {admin && <span>Outstanding balances: <b>{money(data?.outstanding_balance || 0)}</b> on {data?.outstanding_count || 0} cards</span>}
      </div>
      {rows.length === 0 ? <p className="text-sm text-[#8FA89B]" data-testid="gift-redemptions-empty">No gift card redemptions yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-xs text-[#4A5D6B] border-b border-[#E5E0D8]"><th className="py-2">Date</th><th>Code</th><th>For</th><th>Delivered by</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#F1EDE6]" data-testid={`gift-redemption-${r.id}`}>
                  <td className="py-2 whitespace-nowrap">{(r.at || "").slice(0, 10)}</td>
                  <td className="font-mono text-xs">{r.code}</td>
                  <td>{r.label || KIND[r.kind] || r.kind}</td>
                  <td>{[r.enhancer_name, r.location_name].filter(Boolean).join(" · ") || "EYW"}</td>
                  <td className="text-right">{money(r.amount)}{r.status === "returned" && <span className="block text-xs text-[#3F6B55]" data-testid={`gift-redemption-returned-${r.id}`}>Returned to card</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {admin && <ExpiryTool />}
    </section>
  );
};

const ExpiryTool = () => {
  const [code, setCode] = useState("");
  const [date, setDate] = useState("");
  const save = async () => {
    try {
      const { data: card } = await api.get(`/admin/gift-cards/lookup/${encodeURIComponent(code.trim().toUpperCase())}`);
      await api.put(`/admin/gift-cards/${card.id}/expiry`, { expires_at: date });
      toast.success(date ? `Expires ${date}` : "Expiry removed — card never expires");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-6 pt-4 border-t border-[#E5E0D8]" data-testid="gift-expiry-tool">
      <p className="text-xs font-semibold text-[#4A5D6B] uppercase tracking-wide mb-2 flex items-center gap-1.5"><CalendarX size={12} /> Set an expiry date (new cards expire 1 year from purchase)</p>
      <div className="flex flex-wrap gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="EYW-XXXX-XXXX-XXXX" className="eyw-input !py-1.5 text-sm uppercase" data-testid="gift-expiry-code" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="eyw-input !py-1.5 text-sm" data-testid="gift-expiry-date" />
        <button onClick={save} disabled={!code.trim()} className="eyw-btn-ghost !py-1.5 !px-3 text-xs" data-testid="gift-expiry-save">Save</button>
      </div>
    </div>
  );
};
